import React, { useId, useMemo, useRef, useState } from 'react'
import { AuthOption } from './auth-card'
import { formatDateTime, formatLastSignedIn } from './format'
import { deletionDay, restorable } from '../organization-deletion/organization-deletion'
import { SEARCH_FIELD_PROPS } from '../component/search-field-props'

// The one organization chooser: after a single sign-on (/open/organization),
// after a password or device sign-in that has to ask (/auth/account), and the
// in-app "Change Organization" (/auth/account too). Every row says when the
// person last signed in to that organization ("5 minutes ago", "Not opened
// yet"); one row can carry a flag pill ("Last used", "Current").
//
// Many organizations (owner feedback 2026-09-27; the old table could search
// and scroll): past SEARCH_THRESHOLD rows the chooser shows a search field
// (filters by name) and the list scrolls inside a bounded height. Arrow keys,
// Home and End move between rows (and ArrowDown from the search field into
// the list); Enter in the search field picks the only match. Rows are plain
// buttons and off-screen ones skip layout (content-visibility), so hundreds
// of organizations stay quick.
//
// organizations: the auth service's organization rows ({organizationID,
// name, logoImageURL, lastSignedInAt}); lastSignedInAt is RFC 3339 or null.
// A joinable row (SSO join policy "choose") is one the person is not in yet:
// it says so instead of when it was last signed in to, and is never flagged.
// A row scheduledForDeletion carries the "Scheduled for deletion" badge. The
// admin list gives one to every member, whatever their role, until the
// organization is purged (scheduled-org member experience, D5). A row the
// server marks restorable (its owner, on the admin client, not impersonated)
// offers Restore: picking it switches into the restricted restore session,
// which opens the restore page. Any other offers Details: the page shows the
// information sheet instead of switching.
//
// logoImageURL: auth builds it for scheduled rows even with no logo, as the
// bare bucket prefix (…/assets/); a URL ending in "/" is no logo.
//
// Recent (owner 2026-10-07, /auth/account only: `recent`): in a list long
// enough to search, with at least two organizations opened, the five opened
// most recently sit above the rest under "Recent", newest first, and every
// organization stays in "All organizations" below in the server's order (by
// name, live before scheduled). Never-opened, joinable and scheduled rows
// are never recent. While searching the two collapse into one list, best
// match first, then the most recently opened (searchOrganizations); clearing
// the search brings the sections back.
//
// currentKey: the organization this session is in (switching). Its row says
// "Current", carries aria-current and no chevron -- it is not somewhere to
// switch to; the page decides what choosing it does (it goes back). In one
// list, it and the flagged row come first.

export const orgKey = organization => String(organization.organizationID ?? organization.id ?? organization.name)

// The organization signed in to most recently, by lastSignedInAt; null when
// none has ever been signed in to.
export const mostRecentKey = organizations => {
  let best = null
  let bestAt = -Infinity

  for (const organization of organizations || []) {
    const at = organization.lastSignedInAt ? new Date(organization.lastSignedInAt).getTime() : NaN
    if (!Number.isNaN(at) && at > bestAt) {
      best = orgKey(organization)
      bestAt = at
    }
  }

  return best
}

// The flagged organization first; the rest keep the server's order.
export const flaggedFirst = (organizations, flagKey) =>
  [...(organizations || [])].sort((a, b) => (orgKey(b) === flagKey) - (orgKey(a) === flagKey))

export const LastSignedIn = ({ value, now }) =>
  <span title={value ? formatDateTime(value) : undefined}>
    {value && <span className="visually-hidden">Last signed in </span>}
    {formatLastSignedIn(value, now)}
  </span>

export const SEARCH_THRESHOLD = 7

// Organizations whose name contains the query, case- and accent-blind.
const fold = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const filterOrganizations = (organizations, query) => {
  const q = fold(query).trim()
  if (!q) return organizations || []
  return (organizations || []).filter(organization => fold(organization.name).includes(q))
}

// When the organization was last opened, in ms; -Infinity for never.
const openedAt = organization => {
  const at = organization && organization.lastSignedInAt ? new Date(organization.lastSignedInAt).getTime() : NaN
  return Number.isNaN(at) ? -Infinity : at
}

// Newest first; ties (and never-opened rows) keep the order they came in.
const byRecency = (a, b) => {
  const at = openedAt(a.organization)
  const bt = openedAt(b.organization)
  return at === bt ? 0 : bt > at ? 1 : -1
}

export const RECENT_LIMIT = 5
export const RECENT_MIN = 2

// The organizations opened most recently, newest first, at most `limit`:
// live rows the person has opened (never joinable or scheduled ones). None
// when fewer than RECENT_MIN have been opened -- one is no list.
export const recentOrganizations = (organizations, limit = RECENT_LIMIT) => {
  const opened = (organizations || [])
    .filter(organization => !organization.scheduledForDeletion && !organization.joinable && openedAt(organization) > -Infinity)
    .map((organization, index) => ({ organization, index }))
    .sort((a, b) => byRecency(a, b) || a.index - b.index)
    .map(entry => entry.organization)
  return opened.length < RECENT_MIN ? [] : opened.slice(0, limit)
}

// How well a folded name matches a folded query: 0 the name starts with it,
// 1 a word in the name does, 2 it is anywhere else; -1 no match.
const matchRank = (name, q) => {
  let at = name.indexOf(q)
  if (at < 0) return -1
  if (at === 0) return 0
  while (at > 0) {
    if (!/[\p{L}\p{N}]/u.test(name.charAt(at - 1))) return 1
    at = name.indexOf(q, at + 1)
  }
  return 2
}

// The search results as one list: live rows by match, then most recently
// opened, then the server's order; scheduled rows after them in the
// server's order, as in the full list. A blank query keeps the list as is.
export const searchOrganizations = (organizations, query) => {
  const q = fold(query).trim()
  if (!q) return organizations || []
  return (organizations || [])
    .map((organization, index) => ({ organization, index, rank: matchRank(fold(organization.name), q) }))
    .filter(entry => entry.rank >= 0)
    .sort((a, b) => {
      const scheduled = !!a.organization.scheduledForDeletion - !!b.organization.scheduledForDeletion
      if (scheduled || a.organization.scheduledForDeletion) return scheduled || a.index - b.index
      return a.rank - b.rank || byRecency(a, b) || a.index - b.index
    })
    .map(entry => entry.organization)
}

const optionButtons = list => (list ? Array.from(list.querySelectorAll('button.sa-option:not(:disabled)')) : [])

export const OrganizationChooser = ({
  organizations,
  flagKey,
  flagLabel = 'Last used',
  currentKey,
  recent: withRecent = false,
  labelledBy,
  disabled,
  onSelect,
  searchThreshold = SEARCH_THRESHOLD
}) => {
  const [query, updateQuery] = useState('')
  const listRef = useRef(null)
  const searchRef = useRef(null)
  const headingID = useId()

  const all = useMemo(() => organizations || [], [organizations])
  const many = all.length > searchThreshold
  const searching = many && fold(query).trim() !== ''
  const recent = useMemo(() => (withRecent && many ? recentOrganizations(all) : []), [withRecent, many, all])
  const sections = recent.length > 0 && !searching
  const firstKey = currentKey != null ? String(currentKey) : flagKey
  const shown = useMemo(() => {
    if (searching) return withRecent ? searchOrganizations(all, query) : filterOrganizations(flaggedFirst(all, firstKey), query)
    return sections ? all : flaggedFirst(all, firstKey)
  }, [all, searching, withRecent, query, sections, firstKey])
  const now = Date.now()

  const focusOption = index => {
    const buttons = optionButtons(listRef.current)
    if (!buttons.length) return
    buttons[Math.max(0, Math.min(buttons.length - 1, index))].focus()
  }

  const handleListKey = event => {
    const buttons = optionButtons(listRef.current)
    const at = buttons.indexOf(document.activeElement)
    if (at < 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusOption(at + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        if (at === 0 && searchRef.current) searchRef.current.focus()
        else focusOption(at - 1)
        break
      case 'Home':
        event.preventDefault()
        focusOption(0)
        break
      case 'End':
        event.preventDefault()
        focusOption(buttons.length - 1)
        break
      default:
    }
  }

  const handleSearchKey = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusOption(0)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (searching && shown.length === 1 && !disabled) onSelect && onSelect(shown[0])
    } else if (event.key === 'Escape' && query) {
      event.preventDefault()
      updateQuery('')
    }
  }

  const option = organization =>
    <li key={orgKey(organization)}>
      {organization.scheduledForDeletion
        ? <AuthOption
            logo={<OrganizationLogo organization={organization} />}
            name={organization.name}
            flag="Scheduled for deletion"
            flagTone="warn"
            sub={organization.purgeAfter ? `Permanently deleted on ${deletionDay(organization.purgeAfter)}` : undefined}
            action={restorable(organization) ? 'Restore' : 'Details'}
            disabled={disabled}
            onClick={() => onSelect && onSelect(organization)}
          />
        : <AuthOption
            logo={<OrganizationLogo organization={organization} />}
            name={organization.name}
            flag={flagFor(organization, currentKey, flagKey, flagLabel)}
            current={isCurrent(organization, currentKey)}
            sub={organization.joinable ? 'Join this organization' : <LastSignedIn value={organization.lastSignedInAt} now={now} />}
            disabled={disabled}
            onClick={() => onSelect && onSelect(organization)}
          />}
    </li>

  return (
    <div className="sa-chooser">
      {many &&
        <div className="sa-chooser-search">
          <label className="visually-hidden" htmlFor="sa-org-search">Search organizations</label>
          <input
            ref={searchRef}
            {...SEARCH_FIELD_PROPS}
            id="sa-org-search"
            name="org-chooser-search"
            className="form-control"
            placeholder={`Search ${all.length} organizations`}
            value={query}
            onChange={e => updateQuery(e.target.value)}
            onKeyDown={handleSearchKey}
          />
          <p className="visually-hidden" role="status" aria-live="polite">
            {query ? `${shown.length} of ${all.length} organizations` : ''}
          </p>
        </div>}

      {many && shown.length === 0 &&
        <p className="sa-hint sa-chooser-empty">No organizations match “{query.trim()}”.</p>}

      {sections
        ? <div ref={listRef} className="sa-options-scroll sa-chooser-sections" onKeyDown={handleListKey}>
            <div className="sa-chooser-section">
              <h2 className="sa-chooser-heading" id={`${headingID}-recent`}>Recent</h2>
              <ul className="sa-options" aria-labelledby={`${headingID}-recent`}>
                {recent.map(option)}
              </ul>
            </div>
            <div className="sa-chooser-section">
              <h2 className="sa-chooser-heading" id={`${headingID}-all`}>All organizations</h2>
              <ul className="sa-options" aria-labelledby={`${headingID}-all`}>
                {all.map(option)}
              </ul>
            </div>
          </div>
        : <ul ref={listRef} className={`sa-options ${many ? 'sa-options-scroll' : ''}`.trim()} aria-labelledby={labelledBy} onKeyDown={handleListKey}>
            {shown.map(option)}
          </ul>}
    </div>
  )
}

const isCurrent = (organization, currentKey) =>
  currentKey != null && !organization.joinable && orgKey(organization) === String(currentKey)

// The pill beside a live row's name: Current wins over the page's flag.
const flagFor = (organization, currentKey, flagKey, flagLabel) => {
  if (isCurrent(organization, currentKey)) return 'Current'
  return flagKey && !organization.joinable && orgKey(organization) === flagKey ? flagLabel : undefined
}

export const hasLogo = url => !!url && !String(url).endsWith('/')

export const OrganizationLogo = ({ organization }) => {
  const [broken, updateBroken] = useState(false)

  if (hasLogo(organization.logoImageURL) && !broken) {
    return <img className="sa-org-logo" src={organization.logoImageURL} alt="" onError={() => updateBroken(true)} />
  }

  return <span className="sa-initial" aria-hidden="true">{(organization.name || '?').trim().charAt(0).toUpperCase()}</span>
}
